import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import queryString from 'query-string'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import {
  useCreateSavedFilter,
  useDeleteSavedFilter,
  useListSavedFilters,
  useUpdateSavedFilter,
} from 'src/components/async/Account/account.hooks'
import { useFilterLabels } from 'src/components/Reusable/IndexPage/FilterBar/FilterLabelsContext'
import { formatISODate } from 'src/Utils/formatDate'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import { DeleteSVG } from 'src/components/Icons/Basic'
import palette from 'src/designSystem/variables/palette'

// ---------------------------------------------------------------------------
// Route key — strips /{pageContext}/ prefix
// ---------------------------------------------------------------------------

const useFilterRoute = (): string => {
  const { pathname } = useLocation()
  const { pageContext } = useParams<{ pageContext: string }>()
  const stripped =
    pageContext && pathname.startsWith(`/${pageContext}/`)
      ? pathname.slice(`/${pageContext}/`.length)
      : pathname.replace(/^\//, '')
  // Cap at 2 segments — anything deeper is an entity ID (e.g. show/:chatId)
  return stripped.split('/').slice(0, 2).join('/')
}

// ---------------------------------------------------------------------------
// Params serialization — skip pagination-only params
// ---------------------------------------------------------------------------

const SKIP_PARAMS = new Set(['p', 'pc', 'page', 'pageSize'])

// Params saved/restored but not shown in the description
const SKIP_DESCRIPTION_PARAMS = new Set(['dismissed'])

// Params that are scalar (not arrays) in the URL — no bracket notation when restoring
const NO_BRACKET_PARAMS = new Set([
  'search',
  'date_range',
  'dashboard_date_range',
  'contentSearch',
  'dismissed',
])

const serializeCurrentParams = (searchParams: URLSearchParams): string => {
  const record: Record<string, string[]> = {}
  searchParams.forEach((value, key) => {
    const cleanKey = key.replace(/\[]$/, '')
    if (SKIP_PARAMS.has(cleanKey)) return
    if (!record[cleanKey]) record[cleanKey] = []
    record[cleanKey].push(value)
  })
  return JSON.stringify(record)
}

const deserializeParams = (paramsJson: string): Record<string, string[]> => {
  try {
    return JSON.parse(paramsJson) as Record<string, string[]>
  } catch {
    return {}
  }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`

const FilterRow = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid ${palette.neutral[100]};
  border-radius: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover {
    background: ${({ $clickable }) =>
      $clickable ? palette.neutral[30] : 'transparent'};
  }
`

const FilterRowText = styled.div`
  flex: 1;
  min-width: 0;
`

const FilterName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${palette.neutral[900]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FilterDescription = styled.div`
  font-size: 12px;
  color: ${palette.neutral[500]};
  margin-top: 2px;
  white-space: normal;
  word-break: break-word;
`

const TrashButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  color: ${palette.neutral[400]};
  display: flex;
  align-items: center;
  &:hover {
    color: ${palette.magenta[600]};
  }
`

const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${palette.neutral[700]};
`

const ConfirmButton = styled.button`
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid ${palette.magenta[400]};
  background: ${palette.magenta[50]};
  color: ${palette.magenta[700]};
  cursor: pointer;
`

const CancelButton = styled.button`
  padding: 2px 10px;
  font-size: 12px;
  border-radius: 4px;
  border: 1px solid ${palette.neutral[200]};
  background: none;
  color: ${palette.neutral[600]};
  cursor: pointer;
`

const SaveSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${palette.neutral[700]};
`

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid ${palette.neutral[200]};
  border-radius: 6px;
  outline: none;
  box-shadow: none;
  &:focus {
    outline: none;
    box-shadow: none;
  }
`

const CurrentSummary = styled.div`
  font-size: 12px;
  color: ${palette.neutral[500]};
  padding: 6px 10px;
  background: ${palette.neutral[30]};
  border-radius: 6px;
  border: 1px solid ${palette.neutral[100]};
  white-space: normal;
  word-break: break-word;
`

const SectionDivider = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${palette.neutral[400]};
  margin: 12px 0 4px;
`

const EmptyState = styled.div`
  font-size: 13px;
  color: ${palette.neutral[400]};
  text-align: center;
  padding: 16px 0;
`

const StyledDeleteSVG = styled(DeleteSVG)`
  width: 16px;
  height: 16px;
`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  show: boolean
  hide: () => void
  mode: 'save' | 'load'
}

export const SavedFiltersModal = ({ show, hide, mode }: Props): JSX.Element => {
  const route = useFilterRoute()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data, mutate } = useListSavedFilters(route)
  const createSavedFilter = useCreateSavedFilter()
  const updateSavedFilter = useUpdateSavedFilter()
  const deleteSavedFilter = useDeleteSavedFilter()
  const { filters: registeredFilters } = useFilterLabels()

  const filters = useMemo(() => data?.getFiltersList() ?? [], [data])

  const [name, setName] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (show && mode === 'save') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [show, mode])

  const currentParamsJson = useMemo(
    () => serializeCurrentParams(searchParams),
    [searchParams],
  )

  const buildDescription = useCallback(
    (params: Record<string, string[]>): string => {
      const parts: string[] = []
      let searchPart: string | null = null
      Object.entries(params).forEach(([key, ids]) => {
        if (ids.length === 0) return
        if (SKIP_DESCRIPTION_PARAMS.has(key)) return
        if (key === 'date_range' || key === 'dashboard_date_range') {
          const parts2 = ids[0]?.split('-') ?? []
          if (parts2.length === 2) {
            const fmt = (ts: string) => formatISODate(Number(ts))
            parts.push(`Date: ${fmt(parts2[0])} to ${fmt(parts2[1])}`)
          } else {
            parts.push(`Date: ${ids[0]}`)
          }
          return
        }
        if (key === 'search') {
          let searchVal = ids[0]
          try {
            searchVal = decodeURIComponent(searchVal)
          } catch {
            // value contains a literal % — use as-is, already decoded by URLSearchParams
          }
          searchPart = `Search: ${searchVal}`
          return
        }
        const entry = registeredFilters[key]
        if (entry) {
          const labels = ids.map((id) => entry.valueMap[id] ?? id).join(', ')
          parts.push(`${entry.displayName}: ${labels}`)
        } else {
          // Custom dropdown not registered — show formatted filter name + raw values
          const displayName = key
            .replace(/_ids?$/, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
          parts.push(`${displayName}: ${ids.join(', ')}`)
        }
      })
      parts.sort((a, b) => a.localeCompare(b))
      if (searchPart) parts.push(searchPart)
      return parts.join(' · ')
    },
    [registeredFilters],
  )

  const currentDescription = useMemo(
    () => buildDescription(deserializeParams(currentParamsJson)),
    [buildDescription, currentParamsJson],
  )

  const existingMatch = useMemo(
    () => filters.find((f) => f.getName() === name.trim()),
    [filters, name],
  )

  const handleHide = useCallback(() => {
    setName('')
    setConfirmDeleteId(null)
    hide()
  }, [hide])

  const handleSave = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setIsSaving(true)
    try {
      if (existingMatch) {
        await updateSavedFilter(
          existingMatch.getId(),
          trimmed,
          currentParamsJson,
        )
      } else {
        await createSavedFilter(route, trimmed, currentParamsJson)
      }
      await mutate()
      handleHide()
    } finally {
      setIsSaving(false)
    }
  }, [
    name,
    existingMatch,
    currentParamsJson,
    route,
    createSavedFilter,
    updateSavedFilter,
    mutate,
    handleHide,
  ])

  const handleLoad = useCallback(
    (paramsJson: string) => {
      const params = deserializeParams(paramsJson)
      const query: Record<string, string | string[]> = {}
      Object.entries(params).forEach(([key, vals]) => {
        query[key] = NO_BRACKET_PARAMS.has(key) ? vals[0] : vals
      })
      const url = queryString.stringifyUrl(
        { url: pathname, query },
        { arrayFormat: 'bracket' },
      )
      navigate(url, { replace: true })
      handleHide()
    },
    [navigate, pathname, handleHide],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteSavedFilter(id)
      await mutate()
      setConfirmDeleteId(null)
    },
    [deleteSavedFilter, mutate],
  )

  const isSaveMode = mode === 'save'

  return (
    <ModalForm
      show={show}
      hide={handleHide}
      formId={ModalFormId.SAVED_FILTERS}
      title={isSaveMode ? 'Save filters' : 'Load filters'}
      size="lg"
      hideButtons={!isSaveMode}
      hideDeactivate
      scrollContent
      submitText="Save"
      disableSubmitButton={isSaving || !name.trim()}
      busy={isSaving}
    >
      {isSaveMode && (
        <form
          id={ModalFormId.SAVED_FILTERS}
          onSubmit={(e) => {
            e.preventDefault()
            handleSave()
          }}
        >
          <SaveSection>
            <Label>Name</Label>
            <Input
              ref={nameInputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSave()
                }
              }}
              placeholder="e.g. Open controls, SOC 2 gaps..."
            />
            {existingMatch && (
              <div style={{ fontSize: 12, color: palette.orange[700] }}>
                A filter named &ldquo;{existingMatch.getName()}&rdquo; already
                exists — saving will overwrite it.
              </div>
            )}
            <Label style={{ marginTop: 4 }}>Current filters</Label>
            <CurrentSummary title={currentDescription || 'No active filters'}>
              {currentDescription || 'No active filters'}
            </CurrentSummary>
            {filters.length > 0 && (
              <SectionDivider>Or overwrite an existing filter</SectionDivider>
            )}
          </SaveSection>
        </form>
      )}

      {filters.length === 0 ? (
        <EmptyState>No saved filters yet.</EmptyState>
      ) : (
        <FilterList>
          {filters.map((f) => (
            <FilterRow
              key={f.getId()}
              $clickable={!isSaveMode}
              onClick={
                !isSaveMode ? () => handleLoad(f.getParamsJson()) : undefined
              }
            >
              {confirmDeleteId === f.getId() ? (
                <ConfirmRow>
                  <span>Delete &ldquo;{f.getName()}&rdquo;?</span>
                  <ConfirmButton
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(f.getId())
                    }}
                  >
                    Delete
                  </ConfirmButton>
                  <CancelButton
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmDeleteId(null)
                    }}
                  >
                    Cancel
                  </CancelButton>
                </ConfirmRow>
              ) : (
                <>
                  <FilterRowText
                    onClick={
                      isSaveMode ? () => setName(f.getName()) : undefined
                    }
                    style={{ cursor: isSaveMode ? 'pointer' : 'inherit' }}
                  >
                    <FilterName>{f.getName()}</FilterName>
                    <FilterDescription
                      title={
                        buildDescription(
                          deserializeParams(f.getParamsJson()),
                        ) || 'No filters'
                      }
                    >
                      {buildDescription(deserializeParams(f.getParamsJson())) ||
                        'No filters'}
                    </FilterDescription>
                  </FilterRowText>
                  <TrashButton
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmDeleteId(f.getId())
                    }}
                    title="Delete"
                  >
                    <StyledDeleteSVG />
                  </TrashButton>
                </>
              )}
            </FilterRow>
          ))}
        </FilterList>
      )}
    </ModalForm>
  )
}
